import type {
  User,
  KnowledgeEntry,
  EntryDetail,
  Message,
  Chat,
  ChatDetail,
  ChatMessageResponse,
} from "../types"

class ApiClient {
  private baseUrl = "/api"

  private getCsrfToken(): string | null {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : null
  }

  private async fetch<T>(url: string, options: RequestInit = {}): Promise<T> {
    const csrfToken = this.getCsrfToken()
    const response = await fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        ...options.headers,
      },
    })

    if (response.status === 401) {
      window.location.href = "/login"
      throw new Error("Unauthorized")
    }

    if (response.status === 204) {
      return null as T
    }

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Unknown error" }))
      throw new Error(error.error || "Request failed")
    }

    return response.json()
  }

  async login(email: string, password: string): Promise<User> {
    return this.fetch<User>(`${this.baseUrl}/session`, {
      method: "POST",
      body: JSON.stringify({ email, password }),
    })
  }

  async logout(): Promise<void> {
    return this.fetch<void>(`${this.baseUrl}/session`, {
      method: "DELETE",
    })
  }

  async getEntries(params?: {
    status?: string
    q?: string
  }): Promise<KnowledgeEntry[]> {
    const searchParams = new URLSearchParams()
    if (params?.status) {
      searchParams.append("status", params.status)
    }
    if (params?.q) {
      searchParams.append("q", params.q)
    }

    const query = searchParams.toString()
    const url = query
      ? `${this.baseUrl}/entries?${query}`
      : `${this.baseUrl}/entries`

    return this.fetch<KnowledgeEntry[]>(url)
  }

  async getEntry(id: string): Promise<EntryDetail> {
    return this.fetch<EntryDetail>(`${this.baseUrl}/entries/${id}`)
  }

  async updateEntry(
    id: string,
    data: Partial<Pick<KnowledgeEntry, "title" | "notes" | "status">>,
  ): Promise<EntryDetail> {
    return this.fetch<EntryDetail>(`${this.baseUrl}/entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    })
  }

  async syncFromUrl(url: string): Promise<KnowledgeEntry> {
    return this.fetch<KnowledgeEntry>(`${this.baseUrl}/entries/sync_from_url`, {
      method: "POST",
      body: JSON.stringify({ url }),
    })
  }

  async toggleSolution(entryId: string, messageId: string): Promise<Message> {
    return this.fetch<Message>(
      `${this.baseUrl}/entries/${entryId}/messages/${messageId}/toggle_solution`,
      {
        method: "PATCH",
      },
    )
  }

  async toggleEmbedding(entryId: string, messageId: string): Promise<Message> {
    return this.fetch<Message>(
      `${this.baseUrl}/entries/${entryId}/messages/${messageId}/toggle_embedding`,
      {
        method: "PATCH",
      },
    )
  }

  async getUsers(): Promise<User[]> {
    return this.fetch<User[]>(`${this.baseUrl}/admin/users`)
  }

  async createUser(data: {
    email: string
    name: string
    password: string
  }): Promise<User> {
    return this.fetch<User>(`${this.baseUrl}/admin/users`, {
      method: "POST",
      body: JSON.stringify({ user: data }),
    })
  }

  async updateUser(
    id: string,
    data: { name?: string; password?: string },
  ): Promise<User> {
    return this.fetch<User>(`${this.baseUrl}/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ user: data }),
    })
  }

  async deleteEntry(id: string): Promise<void> {
    return this.fetch<void>(`${this.baseUrl}/admin/entries/${id}`, {
      method: "DELETE",
    })
  }

  async getChats(): Promise<Chat[]> {
    return this.fetch<Chat[]>(`${this.baseUrl}/chats`)
  }

  async getChat(id: string): Promise<ChatDetail> {
    return this.fetch<ChatDetail>(`${this.baseUrl}/chats/${id}`)
  }

  async createChat(title?: string): Promise<ChatDetail> {
    return this.fetch<ChatDetail>(`${this.baseUrl}/chats`, {
      method: "POST",
      body: JSON.stringify({ title }),
    })
  }

  async deleteChat(id: string): Promise<void> {
    return this.fetch<void>(`${this.baseUrl}/chats/${id}`, {
      method: "DELETE",
    })
  }

  async sendChatMessage(
    chatId: string,
    content: string,
  ): Promise<ChatMessageResponse> {
    return this.fetch<ChatMessageResponse>(
      `${this.baseUrl}/chats/${chatId}/message`,
      {
        method: "POST",
        body: JSON.stringify({ content }),
      },
    )
  }
}

export const apiClient = new ApiClient()
